import css from 'styled-jsx/css';
import { pageWidth, sansSerif } from 'styles/css-constants';
import { sharedPalette, warmGrays } from 'styles/colors/constants';
import { MQ_MEDIUM, MQ_LARGE, MQ_MIDLARGE } from 'constants/mq';
import { hexToRgbValues, rgbaToHslaValues } from 'util/colors';

export default css`
  .SiteHeader {
    position: relative;
    z-index: 1000;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
  }

  .SiteHeader--recipeBox {
    margin-bottom: -2.5rem;
    z-index: 0;
    background-color: transparent;
  }

  .SiteHeader__actions {
    margin: 0 1rem;
    display: flex;
  }

  .SiteHeader__cancelBtnContainer {
    display: flex;
    justify-content: center;
  }

  .SiteHeader__cancelBtnContainer button {
    display: inline-block;
    padding: 0.5rem 0.5rem;
    background-color: ${sharedPalette.watermelon};
    color: #fff;
    font-size: var(--fontScaleNeg2);
    text-align: center;
    text-transform: uppercase;
    outline: none;
    cursor: pointer;
    min-height: 38px;
  }

  .SiteHeader__icon {
    color: ${warmGrays[7]};
    font-size: 20px;
  }

  .SiteHeader__icon:hover,
  .SiteHeader__newsletterLink:hover {
    color: ${warmGrays[9]};
  }

  .SiteHeader__icon--recipeBox {
    font-size: 40px;
  }

  .SiteHeader__logo {
    flex: 1 0 0px;
    margin: 0.5rem 0 0.5rem 1rem;
    display: flex;
    align-items: flex-end;
  }

  .SiteHeader__logo :global(a) {
    display: flex;
    justify-content: flex-start;
  }

  .SiteHeader__logo :global(svg) {
    display: block;
    height: 22px;
  }

  .SiteHeader__logo span {
    font-size: 15px;
    text-transform: uppercase;
    padding-left: 1rem;
  }

  .SiteHeader__nav {
    flex: 0 0 100%;
    border-top: 1px solid ${warmGrays[0]};
  }

  .SiteHeader__nav :global(.SiteHeader__nav-item:nth-child(n + 4)) {
    display: none;
  }

  .SiteHeader__newsletter,
  .SiteHeader__user {
    display: flex;
  }

  .SiteHeader__newsletterLink {
    display: flex;
    align-items: center;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    color: ${warmGrays[7]};
    font-family: ${sansSerif};
    font-size: var(--fontScaleNeg3);
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .SiteHeader__search {
    display: none;
  }

  .SiteHeader__search-button {
    display: flex;
    align-items: center;
    padding-left: 1rem;
  }

  .SiteHeader__searchBoxContainer {
    display: flex;
    width: 100%;
  }

  .SiteHeader__user {
    position: relative;
    width: 36px;
  }

  @supports (-webkit-touch-callout: none) {
    /* CSS specific to iOS devices */
    .SiteHeader__cancelBtnContainer button {
      min-height: 34px;
    }
  }

  @media screen and ${MQ_MEDIUM} {
    .SiteHeader {
      /* explicit height for as reference for positioning the flyout */
      height: 70px;
    }

    .SiteHeader--no-nav {
      height: inherit;
    }

    .SiteHeader__actions {
      margin-right: 1.5rem;
    }

    .SiteHeader__logo {
      margin-left: 1.5rem;
    }

    .SiteHeader__logo :global(svg) {
      height: 24px;
    }

    .SiteHeader--searchPage,
    .SiteHeader--recipeBox {
      margin-bottom: -2.125rem;
      z-index: 0;
      background-color: transparent;
    }
  }

  @supports (display: grid) {
    .SiteHeader--centered {
      display: grid;
      grid-template: '. logo actions'/ 1fr auto 1fr;
      justify-items: center;
    }

    .SiteHeader--centered .SiteHeader__actions {
      grid-area: actions;
    }

    .SiteHeader--centered .SiteHeader__logo {
      grid-area: logo;
      margin-right: 0;
      margin-left: 0;
    }

    @media screen and ${MQ_LARGE} {
      .SiteHeader {
        display: grid;
        grid-template:
          '... logo actions ...'
          'nav nav  nav     nav' / 1fr 488px 488px 1fr;
        /* explicit height for as reference for positioning the flyout */
        height: 80px;
      }

      .SiteHeader--no-nav {
        height: inherit;
      }

      .SiteHeader__actions {
        grid-area: actions;
        justify-self: end;
        margin-right: 0;
        display: flex;
        align-items: center;
      }

      .SiteHeader__logo {
        grid-area: logo;
        margin-left: 0;
        display: flex;
        align-items: flex-end;
      }

      .SiteHeader__logo span {
        font-size: 25px;
        text-transform: uppercase;
        padding-left: 2rem;
      }

      .SiteHeader__logo :global(svg) {
        height: 36px;
      }

      .SiteHeader__nav {
        grid-area: nav;
      }

      .SiteHeader__newsletter {
        /* Optically create even spacing with the account icon and search bar */
        margin-right: 0.6rem;
      }

      .SiteHeader__newsletterLink {
        font-size: var(--fontScaleNeg2);
      }

      .SiteHeader__search,
      .SiteHeader__user {
        display: initial;
      }

      .SiteHeader__search {
        width: 360px;
      }

      .SiteHeader__search-button {
        display: none;
      }

      .SiteHeader__user {
        margin-right: 2rem;
      }

      .SiteHeader :global(.SiteHeaderNav) {
        margin-right: auto;
        margin-left: auto;
        width: ${pageWidth.large};
      }

      .SiteHeader--searchPage {
        margin-bottom: -2.5rem;
      }

      .SiteHeader--recipeBox {
        grid-template-columns: 1rem 50vw 49vw 1rem;
        margin-bottom: -2.5rem;
      }
    }

    @media screen and ${MQ_MIDLARGE} {
      .SiteHeader {
        grid-template-columns: 1fr 574px 574px 1fr;
      }

      .SiteHeader--recipeBox {
        grid-template-columns: 1rem 50vw 49vw 1rem;
      }

      .SiteHeader :global(.SiteHeaderNav) {
        width: ${pageWidth.midlarge};
      }
    }
  }
`;

// Make a shade of watermelon
const watermelonRgb = hexToRgbValues(sharedPalette.watermelon);
const watermelonHsla = rgbaToHslaValues(
  watermelonRgb.red,
  watermelonRgb.green,
  watermelonRgb.blue,
  1
);
watermelonHsla.lightness *= 0.75;

export const accountLinkStyles = css`
  .AccountLink {
    position: relative;
  }

  :global(.AccountLink__button) {
    display: block;
    padding: 0.5rem 0 0.5rem 1rem;
    background-color: transparent;
    color: ${warmGrays[7]};
    font-size: 20px;
    overflow: hidden;
    cursor: pointer;
  }

  .AccountLink__navItem {
    display: block;
    padding: 0.375rem 0.5rem;
    color: ${warmGrays[9]};
    font-size: var(--fontScaleNeg3);
    letter-spacing: 0.02em;
    text-align: center;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .AccountLink__navLogout {
    margin: 0.25rem 0 0;
    border-top: 1px solid ${warmGrays[1]};
    padding-top: 0.5rem;
  }

  .AccountLink__button--signedIn {
    color: ${sharedPalette.watermelon};
  }

  .AccountLink__button--signedIn:hover {
    color: hsl(
      ${watermelonHsla.hue},
      ${watermelonHsla.saturation}%,
      ${watermelonHsla.lightness}%
    );
  }

  @media screen and ${MQ_LARGE} {
    .AccountLink__navItem {
      font-size: calc(var(--fontScaleNeg1) / 1.125);
    }
  }

  /* Tooltip styles */
  .AccountLink__button + :global(.Tooltip) {
    top: 28px;
    right: -1rem;
    padding: 0.5rem 0.75rem;
  }

  .AccountLink__button + :global(.Tooltip::after) {
    right: 1rem;
    left: auto;
    transform: none;
  }
`;
